import React, { useCallback, useEffect, useRef, useState } from "react";
import GoogleMapsReact from "google-map-react";
import Marker from "./Marker";
import { Wrapper } from "./style";
import { Property } from "../../../../global/types";
import { getNeighborhoodCenter } from "../../utils/geoDecisionEngine";
import {
  detectNeighborhoodFromMapCenter,
  hasMapCenterChangedSignificantly,
  NEIGHBORHOOD_DETECTION_CONFIG,
} from "../../utils/neighborhoodDetection";
import { geocodeCity, geocodeNeighborhood } from "../../utils/geoUtils";
import { getCityCenter } from "../../utils/cityCenters";
import { SavedMapView } from "../../utils/mapViewStorage";
import { MapBounds } from "../../utils/mapBoundsUtils";
import {
  buildNeighborhoodFocusKey,
  getNeighborhoodFocusCenter,
  getNeighborhoodMappableProperties,
  getPropertyCoordinates,
  isValidLatLng,
} from "./searchMapNeighborhoodFocus";
import { getSearchMapDistanceToMouse } from "./searchMapDistanceToMouse";
import {
  resolveInitialMapCenter,
  shouldFocusMapOnCity,
  shouldRestoreSavedMapView,
} from "./searchMapCityFocus";

const MARKER_COLOR_DEFAULT = "#6EA6FF";
const MARKER_COLOR_MUTED = "#B8CCE8";

const MAP_CITY_ZOOM = 12;
const MAP_NEIGHBORHOOD_HIGHLIGHT_ZOOM = 15;
const MAP_MAX_ZOOM = 18;

export interface MapViewportPayload {
  bounds: MapBounds;
  center: { lat: number; lng: number };
  zoom: number;
}

interface IProps {
  properties: Property[];
  isMapVisible: boolean;
  mapCenterNeighborhood?: string | null;
  city?: string | null;
  uf?: string | null;
  initialMapView?: SavedMapView | null;
  preferRestoredView?: boolean;
  /** When set, pins in this set are highlighted; others are muted. Null = all default blue. */
  highlightedPropertyIds?: number[] | null;
  onNeighborhoodUrlChange: (neighborhood: string | null) => void;
  onMapViewChange: (view: SavedMapView) => void;
  onViewportChange?: (viewport: MapViewportPayload) => void;
  onMapReady: () => void;
  onMarkerSelect: (propertyId: number, mapZoom?: number) => void;
  onZoomEnd?: (zoom: number) => void;
}

function getMapCenter(map: any): { lat: number; lng: number } | null {
  const center = map?.getCenter?.();
  if (!center) {
    return null;
  }

  const lat = center.lat?.();
  const lng = center.lng?.();

  if (!isValidLatLng(lat, lng)) {
    return null;
  }

  return { lat, lng };
}

function getMapBounds(map: any): MapBounds | null {
  const bounds = map?.getBounds?.();
  if (!bounds?.getNorthEast || !bounds?.getSouthWest) {
    return null;
  }

  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();
  const neLat = northEast?.lat?.();
  const neLng = northEast?.lng?.();
  const swLat = southWest?.lat?.();
  const swLng = southWest?.lng?.();

  if (
    !isValidLatLng(neLat, neLng) ||
    !isValidLatLng(swLat, swLng)
  ) {
    return null;
  }

  return {
    NE: { lat: neLat, lng: neLng },
    SW: { lat: swLat, lng: swLng },
  };
}

export default function SearchMap({
  properties = [],
  isMapVisible,
  mapCenterNeighborhood = null,
  city = null,
  uf = null,
  initialMapView = null,
  preferRestoredView = false,
  highlightedPropertyIds = null,
  onNeighborhoodUrlChange,
  onMapViewChange,
  onViewportChange,
  onMapReady,
  onMarkerSelect,
  onZoomEnd,
}: IProps) {
  const [isMapLoaded, setMapLoaded] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number>();
  const [defaultCenter] = useState(() =>
    resolveInitialMapCenter(mapCenterNeighborhood, city, uf)
  );

  const justSelectedMarkerRef = useRef(false);
  const isDraggingRef = useRef(false);
  const isProgrammaticMoveRef = useRef(false);
  const userHasNavigatedMapRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentNeighborhoodRef = useRef<string | null>(mapCenterNeighborhood);
  const neighborhoodFocusKeyRef = useRef<string | null>(null);
  const hasRestoredMapViewRef = useRef(false);
  const skipNextNeighborhoodFocusRef = useRef(false);
  const googleMapRef = useRef<any>(null);
  const mapsApiRef = useRef<any>(null);
  const mapListenersRef = useRef<any[]>([]);
  const lastCenterRef = useRef<{ lat: number; lng: number } | null>(null);
  const lastZoomRef = useRef<number | null>(null);
  const lastCityFocusKeyRef = useRef<string | null>(null);
  const onMapViewChangeRef = useRef(onMapViewChange);
  const onNeighborhoodUrlChangeRef = useRef(onNeighborhoodUrlChange);
  const onViewportChangeRef = useRef(onViewportChange);
  const onZoomEndRef = useRef(onZoomEnd);
  const onMapReadyRef = useRef(onMapReady);
  const initialMapViewRef = useRef(initialMapView);

  useEffect(() => {
    onMapViewChangeRef.current = onMapViewChange;
  }, [onMapViewChange]);

  useEffect(() => {
    onNeighborhoodUrlChangeRef.current = onNeighborhoodUrlChange;
  }, [onNeighborhoodUrlChange]);

  useEffect(() => {
    onViewportChangeRef.current = onViewportChange;
  }, [onViewportChange]);

  useEffect(() => {
    onZoomEndRef.current = onZoomEnd;
  }, [onZoomEnd]);

  useEffect(() => {
    onMapReadyRef.current = onMapReady;
  }, [onMapReady]);

  useEffect(() => {
    initialMapViewRef.current = initialMapView;
  }, [initialMapView]);

  useEffect(() => {
    currentNeighborhoodRef.current = mapCenterNeighborhood;

    if (!mapCenterNeighborhood) {
      neighborhoodFocusKeyRef.current = null;
    }
  }, [mapCenterNeighborhood]);

  const emitViewportChange = useCallback((map: any) => {
    const center = getMapCenter(map);
    const bounds = getMapBounds(map);
    if (!center || !bounds) {
      return;
    }

    const zoom = map.getZoom?.() || MAP_CITY_ZOOM;
    onViewportChangeRef.current?.({ bounds, center, zoom });
  }, []);

  const detectAndSyncNeighborhood = useCallback(
    (map: any, options: { requireCenterChange?: boolean } = {}) => {
      const { requireCenterChange = false } = options;

      if (justSelectedMarkerRef.current || isProgrammaticMoveRef.current) {
        return;
      }

      const center = getMapCenter(map);
      if (!center) {
        return;
      }

      const centerChanged = hasMapCenterChangedSignificantly(
        lastCenterRef.current,
        center
      );

      lastCenterRef.current = center;

      if (requireCenterChange && !centerChanged) {
        return;
      }

      const zoom = map.getZoom?.() || MAP_NEIGHBORHOOD_HIGHLIGHT_ZOOM;
      const detected = detectNeighborhoodFromMapCenter(center, zoom);

      onMapViewChangeRef.current({
        center,
        zoom,
        neighborhood: detected,
      });

      if (detected === currentNeighborhoodRef.current) {
        return;
      }

      currentNeighborhoodRef.current = detected;
      // Map wrote the URL — do not snap the camera back to neighborhood centroid.
      skipNextNeighborhoodFocusRef.current = true;
      onNeighborhoodUrlChangeRef.current(detected);
    },
    []
  );

  const persistMapView = useCallback((map: any) => {
    const center = getMapCenter(map);
    if (!center) {
      return;
    }

    const zoom = map.getZoom?.() || MAP_NEIGHBORHOOD_HIGHLIGHT_ZOOM;
    lastCenterRef.current = center;

    onMapViewChangeRef.current({
      center,
      zoom,
      neighborhood: currentNeighborhoodRef.current,
    });
  }, []);

  const scheduleUserMapInteraction = useCallback(
    (map: any, options: { syncNeighborhood: boolean }) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        if (isProgrammaticMoveRef.current || justSelectedMarkerRef.current) {
          return;
        }

        // Keep the full city list until the user actually pans or zooms.
        // Initial map settle / programmatic focus must not activate bounds filtering.
        if (!userHasNavigatedMapRef.current) {
          return;
        }

        emitViewportChange(map);

        if (options.syncNeighborhood) {
          detectAndSyncNeighborhood(map, { requireCenterChange: true });
        } else {
          persistMapView(map);
        }
      }, NEIGHBORHOOD_DETECTION_CONFIG.MAP_DEBOUNCE_MS);
    },
    [detectAndSyncNeighborhood, emitViewportChange, persistMapView]
  );

  const releaseProgrammaticMove = useCallback(() => {
    setTimeout(() => {
      isProgrammaticMoveRef.current = false;
    }, 600);
  }, []);

  const centerMapOnNeighborhood = useCallback(
    async (neighborhood: string) => {
      const map = googleMapRef.current;
      if (!map) {
        return;
      }

      let fallbackCenter = getNeighborhoodCenter(neighborhood);

      if (!fallbackCenter && city) {
        fallbackCenter = await geocodeNeighborhood(neighborhood, city, uf || "CE");
      }

      if (!fallbackCenter) {
        return;
      }

      const neighborhoodProperties = getNeighborhoodMappableProperties(
        properties,
        neighborhood
      );
      const focusCenter = getNeighborhoodFocusCenter(
        neighborhoodProperties,
        fallbackCenter
      );

      isProgrammaticMoveRef.current = true;

      if (typeof map.moveCamera === "function") {
        map.moveCamera({
          center: focusCenter,
          zoom: MAP_NEIGHBORHOOD_HIGHLIGHT_ZOOM,
        });
      } else {
        map.setCenter(focusCenter);
        map.setZoom(MAP_NEIGHBORHOOD_HIGHLIGHT_ZOOM);
      }

      lastCenterRef.current = focusCenter;
      lastZoomRef.current = MAP_NEIGHBORHOOD_HIGHLIGHT_ZOOM;
      releaseProgrammaticMove();
    },
    [city, properties, releaseProgrammaticMove, uf]
  );

  const centerMapOnCity = useCallback(
    async (nextCity: string, nextUf: string | null) => {
      const map = googleMapRef.current;
      if (!map) {
        return;
      }

      let cityCenter = getCityCenter(nextCity, nextUf);
      if (!cityCenter) {
        cityCenter = await geocodeCity(nextCity, nextUf);
      }

      if (!cityCenter) {
        return;
      }

      isProgrammaticMoveRef.current = true;
      userHasNavigatedMapRef.current = false;

      if (typeof map.moveCamera === "function") {
        map.moveCamera({
          center: cityCenter,
          zoom: MAP_CITY_ZOOM,
        });
      } else {
        map.setCenter(cityCenter);
        map.setZoom(MAP_CITY_ZOOM);
      }

      lastCenterRef.current = cityCenter;
      lastZoomRef.current = MAP_CITY_ZOOM;
      releaseProgrammaticMove();
    },
    [releaseProgrammaticMove]
  );

  const restoreMapView = useCallback(
    (map: any, view: SavedMapView) => {
      isProgrammaticMoveRef.current = true;
      map.setCenter(view.center);
      map.setZoom(view.zoom);
      currentNeighborhoodRef.current = view.neighborhood;
      releaseProgrammaticMove();
    },
    [releaseProgrammaticMove]
  );

  useEffect(() => {
    if (preferRestoredView || !mapCenterNeighborhood || !isMapLoaded) {
      return;
    }

    const neighborhoodProperties = getNeighborhoodMappableProperties(
      properties,
      mapCenterNeighborhood
    );
    const focusKey = buildNeighborhoodFocusKey(
      mapCenterNeighborhood,
      neighborhoodProperties
    );

    if (skipNextNeighborhoodFocusRef.current) {
      skipNextNeighborhoodFocusRef.current = false;
      neighborhoodFocusKeyRef.current = focusKey;
      return;
    }

    if (neighborhoodFocusKeyRef.current === focusKey) {
      return;
    }

    neighborhoodFocusKeyRef.current = focusKey;
    hasRestoredMapViewRef.current = true;
    centerMapOnNeighborhood(mapCenterNeighborhood);
  }, [
    mapCenterNeighborhood,
    isMapLoaded,
    centerMapOnNeighborhood,
    preferRestoredView,
    properties,
  ]);

  useEffect(() => {
    const { shouldFocus, cityKey } = shouldFocusMapOnCity({
      isMapLoaded,
      preferRestoredView,
      neighborhood: mapCenterNeighborhood,
      city,
      uf,
      previousCityKey: lastCityFocusKeyRef.current,
    });

    if (cityKey) {
      lastCityFocusKeyRef.current = cityKey;
    }

    if (!shouldFocus || !city) {
      return;
    }

    centerMapOnCity(city, uf);
  }, [
    city,
    uf,
    isMapLoaded,
    preferRestoredView,
    mapCenterNeighborhood,
    centerMapOnCity,
  ]);

  useEffect(() => {
    if (selectedPropertyId) {
      justSelectedMarkerRef.current = true;
      const mapZoom = googleMapRef.current?.getZoom?.();
      onMarkerSelect(selectedPropertyId, mapZoom);

      const timeoutId = setTimeout(() => {
        justSelectedMarkerRef.current = false;
      }, 1000);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [selectedPropertyId, onMarkerSelect]);

  const clearMapListeners = useCallback(() => {
    const maps = mapsApiRef.current;
    if (!maps?.event || mapListenersRef.current.length === 0) {
      mapListenersRef.current = [];
      return;
    }

    mapListenersRef.current.forEach((listener) => {
      maps.event.removeListener(listener);
    });
    mapListenersRef.current = [];
  }, []);

  const handleApiLoaded = useCallback(
    (map: any, maps: any) => {
      if (!map || !maps?.event) {
        return;
      }

      setMapLoaded(true);
      googleMapRef.current = map;
      mapsApiRef.current = maps;
      clearMapListeners();

      mapListenersRef.current.push(
        maps.event.addListener(map, "dragstart", () => {
          isDraggingRef.current = true;
          userHasNavigatedMapRef.current = true;
        })
      );

      mapListenersRef.current.push(
        maps.event.addListener(map, "dragend", () => {
          isDraggingRef.current = false;
          scheduleUserMapInteraction(map, { syncNeighborhood: true });
        })
      );

      mapListenersRef.current.push(
        maps.event.addListener(map, "zoom_changed", () => {
          if (isDraggingRef.current || isProgrammaticMoveRef.current) {
            return;
          }

          const currentZoom = map.getZoom?.() || MAP_NEIGHBORHOOD_HIGHLIGHT_ZOOM;

          // Ignore bootstrap zoom_changed from Google Maps until the user zooms.
          if (lastZoomRef.current === null) {
            lastZoomRef.current = currentZoom;
            return;
          }

          if (lastZoomRef.current === currentZoom) {
            return;
          }

          lastZoomRef.current = currentZoom;
          userHasNavigatedMapRef.current = true;
          scheduleUserMapInteraction(map, { syncNeighborhood: false });
          onZoomEndRef.current?.(currentZoom);
        })
      );

      const initialCenter = getMapCenter(map);
      if (initialCenter) {
        lastCenterRef.current = initialCenter;
      }
      lastZoomRef.current = map.getZoom?.() || MAP_NEIGHBORHOOD_HIGHLIGHT_ZOOM;

      const viewToRestore = initialMapViewRef.current;
      if (
        viewToRestore &&
        shouldRestoreSavedMapView({
          preferRestoredView,
          hasRestored: hasRestoredMapViewRef.current,
          hasNeighborhood: !!mapCenterNeighborhood,
          hasSavedView: true,
        })
      ) {
        hasRestoredMapViewRef.current = true;
        restoreMapView(map, viewToRestore);
      }

      onMapReadyRef.current();
    },
    [
      clearMapListeners,
      mapCenterNeighborhood,
      preferRestoredView,
      restoreMapView,
      scheduleUserMapInteraction,
    ]
  );

  const initialMapViewKey = initialMapView
    ? `${initialMapView.center.lat}:${initialMapView.center.lng}:${initialMapView.zoom}:${initialMapView.neighborhood ?? ""}`
    : null;

  useEffect(() => {
    if (!preferRestoredView || !initialMapView || !isMapLoaded) {
      return;
    }

    const map = googleMapRef.current;
    if (!map || hasRestoredMapViewRef.current) {
      return;
    }

    hasRestoredMapViewRef.current = true;
    restoreMapView(map, initialMapView);
    lastCenterRef.current = initialMapView.center;
    lastZoomRef.current = initialMapView.zoom;
  }, [preferRestoredView, initialMapViewKey, initialMapView, isMapLoaded, restoreMapView]);

  const handleMarkerClick = useCallback((propertyId: number) => {
    setSelectedPropertyId(propertyId);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      clearMapListeners();
    };
  }, [clearMapListeners]);

  const mappableProperties = properties.filter(
    (property) => getPropertyCoordinates(property) !== null
  );

  const highlightedIdSet =
    highlightedPropertyIds != null ? new Set(highlightedPropertyIds) : null;

  return (
    <Wrapper
      className={isMapVisible ? "show" : undefined}
      data-testid="search-map"
    >
      {process.env.REACT_APP_GOOGLE_MAPS_KEY && (
        <GoogleMapsReact
          bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAPS_KEY }}
          defaultCenter={defaultCenter}
          defaultZoom={
            mapCenterNeighborhood ? MAP_NEIGHBORHOOD_HIGHLIGHT_ZOOM : MAP_CITY_ZOOM
          }
          options={{
            gestureHandling: "greedy",
            streetViewControl: false,
            fullscreenControl: false,
            maxZoom: MAP_MAX_ZOOM,
            zoomControlOptions: { position: 7 },
          }}
          yesIWantToUseGoogleMapApiInternals
          distanceToMouse={getSearchMapDistanceToMouse}
          onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
        >
          {isMapLoaded
            ? mappableProperties.map((property) => {
                const coordinates = getPropertyCoordinates(property)!;
                const isHighlighted =
                  highlightedIdSet === null || highlightedIdSet.has(property.id);
                const markerColor = isHighlighted
                  ? MARKER_COLOR_DEFAULT
                  : MARKER_COLOR_MUTED;

                return (
                  <Marker
                    key={property.id}
                    lat={coordinates.lat}
                    lng={coordinates.lng}
                    color={markerColor}
                    active={property.id === selectedPropertyId}
                    onClick={() => handleMarkerClick(property.id)}
                  />
                );
              })
            : null}
        </GoogleMapsReact>
      )}
    </Wrapper>
  );
}
